import { ApolloClient } from '@apollo/client';
import { useEffect } from 'react';
import { wsEventBus } from 'src/core/realtime-connection';
import { GET_NOTIFICATION_META_QUERY } from 'src/graphql/entities/notification/notification.operations';
import { NotificationStatus } from 'src/graphql/entities/notification/notification.types';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { isRecordInCacheList } from 'src/graphql/setup/utils';
import { isEntityRelevantToFilters } from 'src/graphql/setup/utils/isEventRelevant';
import { QueryVariables } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { NotificationRaw } from 'src/types';
import { GetNotificationCount } from './use-notification-count';

export const useNotificationCountSubscription = (
  client: ApolloClient<unknown>,
  variables: QueryVariables,
) => {
  const { user } = useCurrentUserProfile();
  const partnerId = useGlobalStore.use.selectedPartnerId();

  useEffect(() => {
    const createSub = wsEventBus
      .onEntity<NotificationRaw>({
        name: 'Notification',
        operation: 'update',
      })
      .subscribe({
        next: (e) =>
          handleUpdateEvent({
            client,
            userId: user?.id || '',
            notification: e.attributes,
            previousNotificationFields: e.previousAttributes,
            variables,
            partnerId,
          }),
      });

    const updateSub = wsEventBus
      .onEntity<NotificationRaw>({
        name: 'Notification',
        operation: 'create',
      })
      .subscribe({
        next: (e) =>
          handleCreateEvent({
            client,
            userId: user?.id || '',
            notification: e.attributes,
            variables,
            partnerId,
          }),
      });

    return () => {
      createSub.unsubscribe();
      updateSub.unsubscribe();
    };
  }, [client, partnerId, user?.id, variables]);
};

function handleCreateEvent(params: {
  client: ApolloClient<unknown>;
  notification: NotificationRaw;
  userId: string;
  variables: QueryVariables;
  partnerId: number | null;
}) {
  const { client, notification, userId, variables, partnerId } = params;

  if (
    notification.userId !== userId ||
    (partnerId !== null && notification.partnerId !== partnerId) ||
    (notification.status as NotificationStatus) === NotificationStatus.Archived
  ) {
    return;
  }

  try {
    if (
      !isEntityRelevantToFilters(notification, variables.params?.filters) ||
      isRecordInCacheList({
        apolloClient: client,
        document: GET_NOTIFICATION_META_QUERY,
        id: notification.id,
        variables,
      })
    ) {
      return;
    }

    client.cache.updateQuery<GetNotificationCount, typeof variables>(
      { query: GET_NOTIFICATION_META_QUERY, variables },
      (existing) => {
        if (!existing) {
          return existing;
        }

        const { records } = existing.getNotification;

        if (records.some((n) => n.id === notification.id)) {
          return existing;
        }

        const newRecords = [
          ...records,
          {
            id: notification.id,
            entityId: notification.entityId,
            entity: notification.entity,
            __typename: 'Notification',
          },
        ];

        return {
          ...existing,
          getNotification: {
            ...existing.getNotification,
            records: newRecords,
          },
        };
      },
    );
  } catch (e) {
    console.error(e);
  }
}

function handleUpdateEvent(params: {
  client: ApolloClient<unknown>;
  notification: NotificationRaw;
  previousNotificationFields: Partial<NotificationRaw>;
  userId: string;
  variables: QueryVariables;
  partnerId: number | null;
}) {
  const { client, notification, userId, variables, previousNotificationFields, partnerId } =
    params;

  if (
    notification.userId !== userId ||
    (partnerId !== null && notification.partnerId !== partnerId) ||
    !('status' in previousNotificationFields)
  ) {
    return;
  }

  try {
    client.cache.updateQuery<GetNotificationCount, typeof variables>(
      { query: GET_NOTIFICATION_META_QUERY, variables },
      (existing) => {
        if (!existing) {
          return existing;
        }

        const { records } = existing.getNotification;

        let newRecords = records;
        if ((notification.status as NotificationStatus) === NotificationStatus.Unread) {
          newRecords = [
            ...records.filter((i) => i.id !== notification.id),
            {
              id: notification.id,
              entityId: String(notification.entityId),
              entity: String(notification.entity),
              __typename: 'Notification',
            },
          ];
        } else {
          newRecords = records.filter((i) => i.id !== notification.id);
        }

        return {
          ...existing,
          getNotification: {
            ...existing.getNotification,
            records: newRecords,
          },
        };
      },
    );
  } catch (e) {
    console.error(e);
  }
}
